import { type Address, CapacityLimitReason, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { previewVaultV2ForceWithdraw, resolveVaultV2ForceWithdrawEligibility } from '@morpho-org/morpho-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { maxUint256 } from 'viem';
import type { VaultV2Gates } from '@/helpers/vault';
import { useWeb3Context } from '@/contexts/Web3Context';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import useVaultV2TooltipMessage from '@/hooks/operation/vault/useVaultV2TooltipMessage';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import type { VaultV2ActionPanelData } from '../VaultV2SimpleActionPanel/vaultV2ActionPanel.mapper';
import { useVaultV2DisabledLabel } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/hooks/useVaultV2DisabledLabel';
import { FORCE_REDEEM_BUFFER_BPS, planForceWithdraw, previewWithdrawShares } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/deallocation';
import { hasInKindRedemptionBundle, inKindRedemptionFeeAssets, inKindRedemptionPenaltyRate, isInKindRedemptionEligibleVault } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/inKindRedemption.helpers';
import { useVaultV2 } from './useVaultV2';
import { useVaultV2Position } from './useVaultV2Position';
import { isInsufficientLiquidityState, isLiquidityLimitedState, resolveWithdrawInputMax } from './withdrawLiquidity.helpers';
type VaultV2ByAddress = Pick<VaultV2ActionPanelData, 'address' | 'asset'> & {
  gates: VaultV2Gates;
};

/**
 * Slack on the penalty-share estimate, as a divisor: 10_000n is 1 bp. Distinct from
 * `FORCE_REDEEM_BUFFER_BPS`, which buffers assets on the deallocation; this covers drift in the
 * share cost of the penalty itself. At the observed ~1.6e-9/s drift 1 bp is ~17 hours.
 */
const PENALTY_DRIFT_HEADROOM_DIVISOR = 10_000n;
const BPS_DENOMINATOR = 10_000n;

/** Shares the deallocation legs will burn as penalty, and how many legs charge one. */
function penaltyBurn(vaultData: Parameters<typeof previewWithdrawShares>[0], deallocations: readonly {
  penaltyAssets: bigint;
}[]) {
  let penaltyShares = 0n;
  let penaltyLegs = 0n;
  for (const d of deallocations) {
    if (d.penaltyAssets <= 0n) continue;
    penaltyShares += previewWithdrawShares(vaultData, d.penaltyAssets);
    penaltyLegs += 1n;
  }
  return {
    penaltyShares,
    penaltyLegs
  };
}
interface UseVaultV2WithdrawLogicParams {
  vaultData: VaultV2ByAddress | null | undefined;
  vaultAddress: Address;
  /** Seeds the input, for a review that re-runs this logic with the panel's amount and MAX choice. */
  initialInput?: {
    amount: bigint;
    isMax: boolean;
  };
}
export function useVaultV2WithdrawLogic({
  vaultData: vaultDataParam,
  vaultAddress,
  initialInput
}: UseVaultV2WithdrawLogicParams) {
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const chainId = vaultDataParam?.asset.chain.id;
  const {
    vaultData,
    vaultDataTimestamp,
    vault,
    isLoading: isVaultLoading,
    isVaultDataPlaceholder,
    isCorrectVaultV2,
    error: vaultDataError,
    totalCoverage,
    forceDeallocateActions,
    refetchVaultData
  } = useVaultV2(vaultAddress);
  const {
    userVaultShares,
    isUserVaultSharesPlaceholder,
    refetch: refetchPosition,
    isLoading: isLoadingPosition,
    error: userVaultPositionError
  } = useVaultV2Position({
    vaultAddress,
    chainId
  });
  const userVaultAssets = useMemo(() => isDefined(userVaultShares) ? vaultData?.toAssets(userVaultShares) : undefined, [vaultData, userVaultShares]);
  const maxWithdrawTotal = useMemo(() => vaultData?.maxWithdraw(maxUint256).value, [vaultData]);
  const forceWithdrawEligibility = useMemo(() => vaultData ? resolveVaultV2ForceWithdrawEligibility(vaultData) : undefined, [vaultData]);
  const sdkMaxExitAssets = useMemo(() => {
    if (!isDefined(vaultData) || !isDefined(vaultDataTimestamp) || !isDefined(userVaultAssets) || userVaultAssets <= ZERO || forceWithdrawEligibility?.type !== 'eligible') return undefined;
    return previewVaultV2ForceWithdraw(vaultData, {
      requestedExitAssets: userVaultAssets,
      timestamp: vaultDataTimestamp,
      userAddress: account.address
    })?.maxExitAssets;
  }, [vaultData, vaultDataTimestamp, userVaultAssets, forceWithdrawEligibility, account.address]);

  // Slider ceiling = normal withdraw + totalCoverage from force-deallocate path.
  const maxWithdraw = useMemo(() => {
    if (!isDefined(userVaultShares)) return undefined;
    if (!isDefined(maxWithdrawTotal) || !isDefined(userVaultAssets) || maxWithdrawTotal >= userVaultAssets) {
      return vaultData?.maxWithdraw(userVaultShares);
    }
    if (userVaultAssets - maxWithdrawTotal <= totalCoverage) {
      return {
        value: userVaultAssets,
        limiter: CapacityLimitReason.balance
      };
    }
    const coverageCeiling = maxWithdrawTotal + totalCoverage;
    // The SDK ceiling accounts for aggregate penalty-leg rounding.
    const ceiling = isDefined(sdkMaxExitAssets) ? MathLib.min(coverageCeiling, sdkMaxExitAssets) : coverageCeiling;
    if (userVaultAssets <= ceiling) return {
      value: userVaultAssets,
      limiter: CapacityLimitReason.balance
    };
    return {
      value: MathLib.min(ceiling, userVaultAssets),
      limiter: CapacityLimitReason.liquidity
    };
  }, [vaultData, userVaultShares, maxWithdrawTotal, userVaultAssets, totalCoverage, sdkMaxExitAssets]);

  // VaultExitBundlesV1 must be deployed on this chain, and the vault must expose exactly one
  // MorphoMarketV1AdapterV2 (VRM-5348) — mirroring the SDK's
  // InKindRedeemRequiresSingleAdapterError / UnsupportedInKindAdapterError build gates.
  //
  // `marketLendPosition` gates it too, because the redemption's output is a direct Market lend
  // position and that flag is what makes such a position visible and withdrawable at all: with
  // it off, `useLendPositionsTableData` masks every one of them and the Market withdraw entry
  // points are hidden, so the success dialog would send the user to a Portfolio that shows
  // neither the position nor a way out of it. The two flags are independent, so the dependency
  // has to be enforced here rather than remembered when one of them is kill-switched.
  const {
    inKindRedemption: isInKindRedemptionFlagEnabled,
    marketLendPosition: isMarketLendPositionEnabled
  } = useFeatureFlags();
  const isInKindRedemptionEligible = isInKindRedemptionFlagEnabled && isMarketLendPositionEnabled && hasInKindRedemptionBundle(targetChainId) && isDefined(vaultData) && isInKindRedemptionEligibleVault(vaultData);

  // With in-kind redemption available the input/Max ceiling is the full position,
  // while `maxWithdraw` stays the serviceable ceiling used for error and callout math.
  const inputMax = useMemo(() => resolveWithdrawInputMax({
    isInKindRedemptionEligible,
    userVaultAssets,
    maxWithdraw
  }), [isInKindRedemptionEligible, userVaultAssets, maxWithdraw]);
  const [inputAmount, setInputAmount] = useState(initialInput?.amount ?? 0n);
  const [isMax, setIsMax] = useState(initialInput?.isMax ?? false);

  // While MAX is active, keep inputAmount synced to the live (accrued) ceiling so accrued
  // interest is withdrawn instead of left as dust (typing untoggles isMax, never overriding it).
  useEffect(() => {
    if (isMax) setInputAmount(inputMax?.value ?? ZERO);
  }, [isMax, inputMax]);
  const isClosePosition = isMax && inputMax?.limiter === CapacityLimitReason.balance;

  // The user asked for the entire position: MAX on a position the vault can serve in full, or the
  // same balance typed out by hand. Typing untoggles MAX, so without the second half a typed full
  // balance falls through to `withdraw(assets)` with no share slack at all — the same defect
  // reached through a different control, not a wider class of them.
  //
  // Deliberately nothing wider. An amount below the balance keeps its own share slack and stays
  // exact, which is what a typed amount asks for. That slack shrinks with the gap, so a request a
  // few wei short of the balance can still round past it on a falling price: the same defect, at a
  // scale nobody transacts at. Widening the boundary to cover it costs the exactness of every
  // typed amount, so it is left alone until something shows it happening.
  const isFullExit = isClosePosition || isDefined(userVaultAssets) && inputAmount > ZERO && inputAmount >= userVaultAssets;
  const onMaxClick = useCallback((value: boolean) => {
    setIsMax(value);
  }, []);
  const isInsufficientLiquidity = useMemo(() => isInsufficientLiquidityState({
    inputAmount,
    userVaultAssets,
    maxWithdraw
  }), [inputAmount, userVaultAssets, maxWithdraw]);

  // Tap-to-autofill on the liquidity callout: drop the input to everything the
  // standard withdraw can serve, which resolves the insufficient-liquidity error.
  const applyServiceableMax = useCallback(() => {
    if (!isDefined(maxWithdraw)) return;
    setIsMax(false);
    setInputAmount(maxWithdraw.value);
  }, [maxWithdraw]);

  // Plan against the on-chain forceDeallocate semantics. Returns null when force-deallocation
  // is unnecessary or infeasible.
  //
  // Full exits redeem shares rather than withdraw assets (see `forceRedeemPlan`), and the redeem is
  // paid at the *execution* share price. When the price rises between the pinned snapshot and the
  // block that lands, the position is worth more than `inputAmount` quotes and the deallocations
  // would come up short, so they are sized against a buffered quote. Buffering only ever
  // over-deallocates, and the surplus stays in vault idle rather than leaving the vault — but the
  // penalty is charged on `D`, so on a penalty-bearing adapter the buffer does cost the user a
  // slightly larger fee (~0.002% of the position at the 2% cap). Free only at zero penalty.
  const planned = useMemo(() => {
    const none = {
      plan: null,
      isBuffered: false
    };
    if (!isDefined(maxWithdrawTotal) || forceDeallocateActions.length === 0) return none;
    const base = {
      availableNormalWithdraw: maxWithdrawTotal,
      actions: forceDeallocateActions
    };
    // Buffer only an exit that genuinely needs force-deallocation. `maxWithdraw` reports the
    // `balance` limiter on a fully liquid vault too, so buffering an amount the normal withdraw
    // already covers would manufacture a fee-bearing plan where `null` is the right answer —
    // charging a penalty nobody owes and turning a plain redeem into one that can revert on
    // adapter liquidity drift.
    if (!isFullExit || inputAmount <= maxWithdrawTotal) {
      return {
        plan: planForceWithdraw({
          inputAmount,
          ...base
        }),
        isBuffered: false
      };
    }
    // Rounded up: integer division yields no buffer at all below 1,000 atomic units, which would
    // still be flagged buffered and sent through a redeem that any upward tick leaves unfunded.
    const buffered = inputAmount + MathLib.mulDivUp(inputAmount, FORCE_REDEEM_BUFFER_BPS, BPS_DENOMINATOR);
    const bufferedPlan = planForceWithdraw({
      inputAmount: buffered,
      ...base
    });
    if (bufferedPlan) return {
      plan: bufferedPlan,
      isBuffered: true
    };
    // Adapter capacity cannot absorb the buffer. Stay on the asset path rather than redeeming
    // against deallocations that only just cover the quote: an unfunded redeem reverts, whereas
    // the asset withdraw is what ships today and still succeeds whenever the share price rises.
    return {
      plan: planForceWithdraw({
        inputAmount,
        ...base
      }),
      isBuffered: false
    };
  }, [maxWithdrawTotal, inputAmount, forceDeallocateActions, isFullExit]);
  const plan = planned.plan;
  const isForceDeallocate = plan !== null;
  const totalPenaltyAssets = plan?.totalPenaltyAssets ?? ZERO;

  // The plan for a full exit may be too large to fund with an exact-share redeem. In that case,
  // fall back to the asset-denominated force-withdraw path when the SDK supports it.
  const forceRedeemPlan = useMemo(() => {
    if (!plan || !isFullExit || !planned.isBuffered) return undefined;
    if (!isDefined(vaultData) || !isDefined(userVaultShares)) return undefined;
    const {
      penaltyShares,
      penaltyLegs
    } = penaltyBurn(vaultData, plan.deallocations);
    const headroom = penaltyShares / PENALTY_DRIFT_HEADROOM_DIVISOR + penaltyLegs;
    const reserved = penaltyShares + headroom;
    if (reserved >= userVaultShares) return undefined;
    return {
      shares: userVaultShares - reserved,
      residualShares: headroom
    };
  }, [plan, planned.isBuffered, isFullExit, vaultData, userVaultShares]);
  const forceWithdrawPreview = useMemo(() => {
    if (!isForceDeallocate || !isDefined(vaultData) || !isDefined(vaultDataTimestamp) || inputAmount <= ZERO || isDefined(forceRedeemPlan) || forceWithdrawEligibility?.type !== 'eligible') {
      return undefined;
    }
    return previewVaultV2ForceWithdraw(vaultData, {
      requestedExitAssets: inputAmount,
      timestamp: vaultDataTimestamp,
      userAddress: account.address
    });
  }, [account.address, forceWithdrawEligibility, forceRedeemPlan, inputAmount, isForceDeallocate, vaultData, vaultDataTimestamp]);

  // The plan for a withdraw sized exactly at the serviceable ceiling. The tier 3 callout
  // quotes what actually reaches the wallet, and past the ceiling `plan` is null because
  // the typed amount cannot be served at all.
  const serviceablePlan = useMemo(() => {
    if (!isDefined(maxWithdrawTotal) || !isDefined(maxWithdraw)) return null;
    if (forceDeallocateActions.length === 0) return null;
    return planForceWithdraw({
      inputAmount: maxWithdraw.value,
      availableNormalWithdraw: maxWithdrawTotal,
      actions: forceDeallocateActions
    });
  }, [maxWithdrawTotal, maxWithdraw, forceDeallocateActions]);

  // The limited variant says the position cannot be served in full, keyed on the position
  // rather than the input so it shows before the user types into an error. It carries the
  // serviceable ceiling, which includes force-deallocate coverage and so can charge a fee: the
  // summary Fee row is what discloses that. It yields to any callout that has something more
  // specific to say about the amount they actually entered.
  //
  // The fee for an exit that cannot be served in full. Without in-kind redemption the exit is
  // still a plain withdraw, stopping at the serviceable ceiling, and the fee reaching that
  // ceiling costs is knowable here even though there is no plan for the typed amount itself.
  const serviceableFeeAssets = serviceablePlan?.totalPenaltyAssets ?? ZERO;

  // With in-kind redemption the fee is the redemption's penalty, computed from the amount the
  // user typed rather than from an SDK preview. It does not depend on which Market they go on to
  // choose: the penalty rate belongs to the adapter, and in-kind eligibility already requires the
  // Vault to expose exactly one, so every Market charges the same rate on the same idle-first
  // split. The only case this figure is wrong is a Market whose capacity caps the exit below the
  // request, and there it overstates the fee, which is the direction the disclosure should err in
  // and the one `formatFeeAmount` already rounds towards. Deriving it arithmetically also keeps
  // the form off the SDK preview, which accrues and sorts every Market on every keystroke.
  const insufficientFeeAssets = isInKindRedemptionEligible && isDefined(vaultData) ? inKindRedemptionFeeAssets(vaultData, inputAmount) : serviceableFeeAssets;

  // Without the insufficient branch the Fee row would disappear on the plain-withdraw path at
  // exactly the tier that charges one, because there is no plan for the amount typed.
  const summaryFeeAssets = isForceDeallocate ? forceWithdrawPreview?.penaltyAssets ?? totalPenaltyAssets : isInsufficientLiquidity ? insufficientFeeAssets : ZERO;

  // The same explanation for the in-kind tier, where there is no plan to read: the fee is the
  // redemption's own, charged on the leg that becomes the Market position. Every figure is
  // derived from the one fee already on screen rather than recomputed, so the sentence
  // reconciles exactly — `netAssets + feeAssets === inKindAssets`. The Figma copy
  // (node 20509:317439) charges the rate on the whole in-kind leg instead, which is the
  // force-deallocate basis; that would not add up here, so the wording names the position.
  const summaryInKindRedemption = useMemo(() => {
    if (!isInsufficientLiquidity || !isInKindRedemptionEligible || !isDefined(vaultData)) {
      return undefined;
    }
    const inKindAssets = MathLib.zeroFloorSub(inputAmount, vaultData.assetBalance);
    if (inKindAssets <= ZERO) return undefined;
    return {
      availableLiquidity: MathLib.min(inputAmount, vaultData.assetBalance),
      withdrawalAmount: inputAmount,
      inKindAssets,
      netAssets: MathLib.zeroFloorSub(inKindAssets, insufficientFeeAssets),
      feeRate: inKindRedemptionPenaltyRate(vaultData)
    };
  }, [isInsufficientLiquidity, isInKindRedemptionEligible, vaultData, inputAmount, insufficientFeeAssets]);

  // The fee tooltip names the whole split, so it only rides along with the plain
  // force-deallocate withdraw: on the insufficient-liquidity tiers the fee on screen belongs
  // to an exit the user has not asked for yet. Adapters can carry different penalties, and a
  // plan spanning two of them has no single rate to name — there the row falls back to the
  // short tooltip rather than quoting one adapter's rate for both.
  const summaryForceDeallocation = useMemo(() => {
    if (forceWithdrawPreview) {
      const feeRate = forceDeallocateActions[0]?.penalty;
      if (!isDefined(feeRate)) return undefined;
      return {
        availableLiquidity: forceWithdrawPreview.assetsToWithdraw,
        withdrawalAmount: forceWithdrawPreview.exitAssets,
        deallocatedAssets: forceWithdrawPreview.assetsToDeallocate,
        feeRate
      };
    }
    if (!plan || !isDefined(maxWithdrawTotal)) return undefined;
    const penalties = new Set(plan.deallocations.map(({
      adapter
    }) => forceDeallocateActions.find(action => action.adapter === adapter)?.penalty));
    const [feeRate_0] = [...penalties];
    if (penalties.size !== 1 || !isDefined(feeRate_0)) return undefined;
    return {
      availableLiquidity: maxWithdrawTotal,
      withdrawalAmount: inputAmount,
      deallocatedAssets: plan.totalDeallocated,
      feeRate: feeRate_0
    };
  }, [plan, maxWithdrawTotal, inputAmount, forceDeallocateActions, forceWithdrawPreview]);
  const liquidityCallout = useMemo(() => {
    if (!isDefined(maxWithdraw)) return undefined;
    if (isInsufficientLiquidity) {
      return {
        variant: 'insufficient' as const,
        assets: maxWithdraw.value,
        // Only the no-redemption variant of this callout renders these two: with in-kind
        // redemption eligible the sentence quotes neither an amount nor a fee. Without it the
        // user can still only take the serviceable ceiling, net of the fee reaching it costs,
        // which is why the wallet leg is priced off the withdraw fee and not the redemption one.
        walletAssets: MathLib.zeroFloorSub(maxWithdraw.value, serviceableFeeAssets),
        feeAssets: serviceableFeeAssets
      };
    }
    if (isLiquidityLimitedState({
      userVaultAssets,
      maxWithdraw
    })) {
      // Withdrawing the whole ceiling force-deallocates, so the wallet receives it net of the
      // penalty. Quoting the ceiling as cash here promised money the transaction never sends —
      // the same defect the insufficient variant was fixed for.
      return {
        variant: 'limited' as const,
        assets: maxWithdraw.value,
        walletAssets: MathLib.zeroFloorSub(maxWithdraw.value, serviceableFeeAssets),
        feeAssets: serviceableFeeAssets
      };
    }
    return undefined;
  }, [maxWithdraw, isInsufficientLiquidity, userVaultAssets, serviceableFeeAssets]);

  // Two reasons this stays quiet. It explains why a *serviceable* withdrawal still costs a fee,
  // so it has nothing to say when the Vault charges none — `main`'s `ForceDeallocatePenaltyWarning`
  // gated itself the same way, and without that a zero-penalty Vault gained a callout it never
  // had. And it yields to the liquidity callout rather than stacking under it: that one already
  // owns this form on a constrained Vault, carries the tap-to-autofill ceiling, and the Figma
  // draws a single callout here (node 19953:247392). What is left is the case it was written for
  // (VAU-1383): a Vault that can serve the position in full, where a withdrawal above idle still
  // deallocates and nothing else on screen says why.
  const showForceDeallocationOrderCallout = isForceDeallocate && totalPenaltyAssets > ZERO && !isDefined(liquidityCallout);

  // Close a position by redeeming shares, never by withdrawing an asset amount. `withdraw(assets)`
  // re-derives the share burn at the execution price, so a quote taken from the block-pinned
  // snapshot rounds up past the user's balance the moment the share price ticks down — which is
  // every block on a vault whose management fee outruns its yield — and the burn reverts with
  // panic 0x11. `redeem(shares)` burns exactly what it is given, at any price.
  //
  // The forceDeallocate legs burn their penalty shares before the redeem runs, so those come off
  // the top. That burn is itself re-derived at execution price, hence the headroom — taken on the
  // penalty estimate alone, never on the position, because the exposure is proportional to the
  // penalty (capped at 2%). At the common zero penalty it is exactly 0 and the position closes to
  // the wei; even at the 2% cap it strands ~0.0002% of the position rather than reverting.
  // What the transaction deliberately leaves deposited: only the headroom survives a force-redeem,
  // since the reserved penalty shares are burned by the deallocation legs and are already reported
  // as the fee. Counting them as retained would show the fee twice — once as a fee, once as a
  // position the user no longer holds. At the common zero penalty this is exactly 0n, and every
  // path other than a buffered force-redeem retains nothing.
  const retainedAssets = useMemo(() => {
    if (!isDefined(forceRedeemPlan) || !isDefined(vaultData)) return ZERO;
    return vaultData.toAssets(forceRedeemPlan.residualShares);
  }, [forceRedeemPlan, vaultData]);
  const finalPosition = useMemo(() => {
    if (!inputAmount || !isDefined(userVaultAssets)) return undefined;
    if (isDefined(forceRedeemPlan)) return retainedAssets;
    if (isFullExit) return ZERO;
    const result = userVaultAssets - inputAmount;
    return result < ZERO ? ZERO : result;
  }, [userVaultAssets, inputAmount, isFullExit, forceRedeemPlan, retainedAssets]);
  const isSnapshotPlaceholder = isUserVaultSharesPlaceholder || isVaultDataPlaceholder;
  const buildTransaction = useCallback(() => {
    // Never build from a bridged snapshot: it is the previous block's vault state
    // (mirrors `useVaultV2InKindRedeemTransaction`).
    if (isSnapshotPlaceholder) return undefined;
    if (!account.address || !isDefined(userVaultShares) || !isDefined(vaultData) || !vault) {
      return undefined;
    }
    if (userVaultShares <= ZERO) return undefined;
    if (inputAmount <= ZERO) return undefined;
    if (isForceDeallocate && plan) {
      // A full exit redeems shares whenever the buffered plan can fund it. Where it cannot, fall
      // through to the asset withdraw that ships today rather than building an unfunded redeem.
      if (isDefined(forceRedeemPlan)) {
        return vault.forceRedeem({
          deallocations: plan.deallocations,
          redeem: {
            shares: forceRedeemPlan.shares
          },
          userAddress: account.address
        });
      }
      if (forceWithdrawEligibility?.type !== 'eligible') return undefined;
      return vault.forceWithdraw({
        exitAssets: inputAmount,
        vaultData,
        userAddress: account.address
      });
    }

    // Same rule off the force path: a full exit redeems shares. A liquid vault needs no
    // deallocation, so `plan` is null here — but `withdraw(userVaultAssets)` carries the identical
    // rounding, and this is the branch most vaults take.
    if (isFullExit) {
      return vault.redeem({
        shares: userVaultShares,
        userAddress: account.address
      });
    }
    return vault.withdraw({
      amount: inputAmount,
      userAddress: account.address,
      vaultData
    });
  }, [account.address, userVaultShares, isSnapshotPlaceholder, vaultData, vault, inputAmount, isFullExit, isForceDeallocate, forceWithdrawEligibility, forceRedeemPlan, plan]);
  const sdkAction = isForceDeallocate && plan ? isDefined(forceRedeemPlan) ? 'forceRedeem' : 'forceWithdraw' : isFullExit ? 'redeem' : 'withdraw';

  // The build is sync — only `getRequirements()` hits RPC, and the review dialog fetches those
  // via `useVaultWithdrawRequirements` once the user asks to review.
  const built = useMemo(() => {
    try {
      return {
        transaction: buildTransaction(),
        buildError: undefined,
        sdkAction
      };
    } catch (error) {
      safeSentryBreadcrumb({
        category: 'transaction',
        message: 'Vault V2 withdraw handle build failed',
        level: 'warning',
        data: {
          chainId: vaultDataParam?.asset.chain.id,
          error: error instanceof Error ? error.message : String(error)
        }
      });
      return {
        transaction: undefined,
        buildError: error,
        sdkAction
      };
    }
  }, [buildTransaction, sdkAction, vaultDataParam?.asset.chain.id]);

  // Each block poll bridges the previous snapshot for a moment, and `buildTransaction` refuses it.
  // The handle built from that same snapshot one render earlier is still sound, so keep serving it
  // while the request is unchanged. A new amount, wallet, chain or vault mid-bridge has nothing to
  // reuse and waits for the fresh snapshot. A MAX built as a share redeem is keyed on intent and
  // share count: re-accrual nudges its amount, but the redeem only depends on the shares, so a
  // balance that settles mid-bridge waits for the fresh snapshot. Mirrors `buildTransaction`.
  const isShareRedeem = isFullExit && (!isForceDeallocate || isDefined(forceRedeemPlan));
  const buildScope = `${vaultAddress}:${targetChainId}:${account.address}:${isMax && isShareRedeem ? `max:${userVaultShares}` : inputAmount}`;
  // The render-phase update settles because `built` is memoized: keep `buildTransaction`'s deps stable.
  const [settled, setSettled] = useState<{
    built: typeof built;
    scope: string;
  }>();
  if (!isSnapshotPlaceholder && (settled?.scope !== buildScope || settled.built !== built)) {
    setSettled({
      built,
      scope: buildScope
    });
  }
  const heldBuild = isSnapshotPlaceholder && settled?.scope === buildScope ? settled.built : undefined;
  const {
    transaction,
    buildError
  } = heldBuild ?? built;
  const localErrorMessage = useMemo(() => {
    if (isDefined(vaultDataError)) return 'Unable to get vault data';
    if (isDefined(buildError)) return 'Unable to prepare withdraw transaction';
    if (isForceDeallocate && !isDefined(forceRedeemPlan) && isDefined(forceWithdrawEligibility) && forceWithdrawEligibility.type !== 'eligible') {
      return 'Force withdrawal is not available for this vault';
    }
    if (!isDefined(maxWithdraw)) return 'Unable to get vault withdraw amount';
    if (!isDefined(userVaultAssets) || isDefined(userVaultPositionError)) {
      return 'Unable to get user vault assets';
    }
    if (inputAmount <= maxWithdraw.value) return undefined;
    if (maxWithdraw.limiter === CapacityLimitReason.balance) return 'Insufficient Position amount';
    if (maxWithdraw.limiter === CapacityLimitReason.liquidity) return 'Insufficient Liquidity';
    return 'Maximum withdraw amount exceeded';
  }, [inputAmount, maxWithdraw, userVaultAssets, userVaultPositionError, vaultDataError, buildError, forceWithdrawEligibility, forceRedeemPlan, isForceDeallocate]);
  const isLoading = isLoadingPosition || isVaultLoading;
  const tooltipMessage = useVaultV2TooltipMessage({
    address: vaultAddress,
    error: localErrorMessage,
    gates: vaultDataParam?.gates,
    chainId
  });
  const disabledLabel = useVaultV2DisabledLabel({
    localErrorMessage,
    isInputEmpty: inputAmount === ZERO,
    isCorrectVaultV2,
    gates: vaultDataParam?.gates
  });

  // Only the button waits, and only when a bridge would otherwise enable it with no handle: the
  // review must not open on an undefined handle. `isLoading` itself stays untouched — it drives
  // input/summary skeletons we don't want flickering every poll.
  const isAwaitingSnapshot = isSnapshotPlaceholder && !isDefined(transaction) && !disabledLabel;
  const isActionLoading = isLoading || isAwaitingSnapshot;

  // IKR entry only when Withdraw is blocked — not as an alternative to a serviceable request.
  // It takes over the primary button, so it may only appear when insufficient liquidity is the
  // *sole* blocker: an exit-gated vault, wrong chain, or pending transaction has to keep its own
  // label.
  const showInKindRedemptionEntry = !isActionLoading && isInsufficientLiquidity && isInKindRedemptionEligible && disabledLabel === localErrorMessage;
  const resetInput = useCallback(() => {
    setInputAmount(ZERO);
    setIsMax(false);
  }, []);
  return {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    resetInput,
    userVaultAssets,
    inputMax,
    isInKindRedemptionEligible,
    liquidityCallout,
    showInKindRedemptionEntry,
    applyServiceableMax,
    finalPosition,
    retainedAssets,
    summaryFeeAssets,
    summaryForceDeallocation,
    summaryInKindRedemption,
    isForceDeallocate,
    showForceDeallocationOrderCallout,
    transaction,
    buildError,
    sdkAction,
    isSnapshotPlaceholder,
    shareToken: vaultData,
    isLoading,
    isActionLoading,
    localErrorMessage,
    disabledLabel,
    tooltipMessage,
    refetchPosition,
    refetchVaultData,
    error: {
      vaultDataError,
      userVaultPositionError
    }
  };
}